import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

export interface PaymentMethod {
  id: string;
  type: string;
  brand?: string;
  last4?: string;
  expMonth?: number;
  expYear?: number;
  isDefault: boolean;
}

export interface Invoice {
  id: string;
  amountCents: number;
  amountDueCents: number;
  amountPaidCents: number;
  currency: string;
  status: string;
  description?: string;
  periodStart?: string;
  periodEnd?: string;
  paidAt?: string;
  createdAt: string;
}

export interface Transaction {
  id: string;
  type: string;
  status: string;
  amountCents: number;
  currency: string;
  description?: string;
  failureMessage?: string;
  processedAt?: string;
  createdAt: string;
}

export interface Subscription {
  id: string;
  status: string;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  trialEnd?: string;
  canceledAt?: string;
  cancelAtPeriodEnd: boolean;
  planId: string;
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  slug: string;
  tier: string;
  amountCents: number;
  currency: string;
  interval: string;
  intervalCount: number;
  trialDays: number;
  metadata?: Record<string, unknown> | null;
}

async function fetchPaymentMethods(): Promise<{ paymentMethods: PaymentMethod[] }> {
  const response = await fetch('/api/payments/methods');
  if (!response.ok) throw new Error('Failed to fetch payment methods');
  return response.json();
}

async function fetchInvoices(): Promise<{ invoices: Invoice[] }> {
  const response = await fetch('/api/payments/invoices');
  if (!response.ok) throw new Error('Failed to fetch invoices');
  return response.json();
}

async function fetchTransactions(): Promise<{ transactions: Transaction[] }> {
  const response = await fetch('/api/payments/transactions');
  if (!response.ok) throw new Error('Failed to fetch transactions');
  return response.json();
}

async function fetchSubscriptions(): Promise<{ subscriptions: Subscription[] }> {
  const response = await fetch('/api/payments/subscriptions');
  if (!response.ok) throw new Error('Failed to fetch subscriptions');
  return response.json();
}

async function fetchPlans(): Promise<{ plans: SubscriptionPlan[] }> {
  const response = await fetch('/api/payments/plans');
  if (!response.ok) throw new Error('Failed to fetch plans');
  return response.json();
}

async function createPaymentMethod(cardDetails: { cardNumber: string; expiry: string; cvv: string }): Promise<unknown> {
  const response = await fetch('/api/payments/methods', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(cardDetails),
  });
  if (!response.ok) throw new Error('Failed to save payment method');
  return response.json();
}

async function deletePaymentMethod(id: string): Promise<unknown> {
  const response = await fetch(`/api/payments/methods/${id}`, { method: 'DELETE' });
  if (!response.ok) throw new Error('Failed to delete payment method');
  return response.json();
}

async function createCharge(payload: {
  amountCents: number;
  paymentMethodId?: string;
  description?: string;
  idempotencyKey?: string;
}): Promise<unknown> {
  const response = await fetch('/api/payments/charge', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error('Charge failed');
  return response.json();
}

async function createSubscription(payload: {
  planId: string;
  paymentMethodId: string;
  trialDays?: number;
}): Promise<unknown> {
  const response = await fetch('/api/payments/subscriptions', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error('Failed to create subscription');
  return response.json();
}

async function cancelSubscription(id: string, atPeriodEnd: boolean): Promise<unknown> {
  const response = await fetch(`/api/payments/subscriptions/${id}/cancel`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ atPeriodEnd }),
  });
  if (!response.ok) throw new Error('Failed to cancel subscription');
  return response.json();
}

async function verifyCheckout(payload: {
  transactionId: string;
  vaultId: string;
  planId: string;
}): Promise<unknown> {
  const response = await fetch('/api/payments/checkout/verify', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to verify checkout');
  }
  return response.json();
}

export interface PaymentConfig {
  checkoutKey: string;
  scriptUrl: string;
  isSandbox: boolean;
}

async function fetchPaymentConfig(): Promise<PaymentConfig> {
  const response = await fetch('/api/payments/config');
  if (!response.ok) throw new Error('Failed to fetch payment config');
  return response.json();
}

export interface CollectCheckoutConfig {
  lineItems: Array<{ lineItemType: string; sku: string; quantity: number }>;
  type: string;
  customerVault: { addCustomer: boolean };
  successUrl: string;
  cancelUrl: string;
  currency: string;
  receipt: { sendToCustomer: boolean };
}

async function initiateCollectCheckout(payload: {
  planId: string;
  redirectUrl: string;
}): Promise<{ success: boolean; config: CollectCheckoutConfig }> {
  const response = await fetch('/api/payments/checkout/collect/initiate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to initiate checkout');
  }

  return response.json();
}

async function verifyCollectCheckout(payload: {
  transactionId: string;
  vaultId: string;
  planId: string;
}): Promise<{ success: boolean; subscriptionId: string; invoiceId: string; transactionId: string; paymentMethodId: string }> {
  const response = await fetch('/api/payments/checkout/collect/verify', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to verify checkout');
  }

  return response.json();
}



export function usePaymentMethods() {
  return useQuery({
    queryKey: ['payment-methods'],
    queryFn: fetchPaymentMethods,
  });
}

export function useInvoices() {
  return useQuery({
    queryKey: ['invoices'],
    queryFn: fetchInvoices,
  });
}

export function useTransactions() {
  return useQuery({
    queryKey: ['transactions'],
    queryFn: fetchTransactions,
  });
}

export function useSubscriptions() {
  return useQuery({
    queryKey: ['subscriptions'],
    queryFn: fetchSubscriptions,
  });
}

export function useSubscriptionPlans() {
  return useQuery({
    queryKey: ['subscription-plans'],
    queryFn: fetchPlans,
  });
}

export function useCreatePaymentMethod() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createPaymentMethod,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['payment-methods'] }),
  });
}

export function useDeletePaymentMethod() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deletePaymentMethod,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['payment-methods'] }),
  });
}

export function useCreateCharge() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createCharge,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}

export function useCreateSubscription() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createSubscription,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}

export function useCancelSubscription() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, atPeriodEnd }: { id: string; atPeriodEnd: boolean }) => cancelSubscription(id, atPeriodEnd),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['subscriptions'] }),
  });
}

export function useVerifyCheckout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: verifyCheckout,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['payment-methods'] });
    },
  });
}

export function useInitiateCollectCheckout() {
  return useMutation({
    mutationFn: initiateCollectCheckout,
  });
}

export function useVerifyCollectCheckout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: verifyCollectCheckout,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['payment-methods'] });
    },
  });
}

export function usePaymentConfig() {
  return useQuery({
    queryKey: ['payment-config'],
    queryFn: fetchPaymentConfig,
    staleTime: 5 * 60 * 1000,
  });
}


